import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useTheme } from '@evi/skins';
import { NORTH_AMERICA, clampCameraLat, validCenter, wrapLng } from '@/lib/mapStartCamera';

const MAPBOX_TOKEN = import.meta.env['VITE_MAPBOX_TOKEN'] as string | undefined;

/**
 * Default per-mode basemap style URLs. Used when the active theme
 * doesn't declare a `mapStyle` override (Default + Luce both
 * inherit the satellite look).
 *
 *   dark  → mapbox/standard-satellite — same Mapbox v3 globe used
 *           by the admin dashboard (5b082cb). Satellite imagery + 3D
 *           globe view + the basemap config schema admin tweaks.
 *   light → mapbox/satellite-streets-v12 — diverges from admin's
 *           light-v11. The PWA is the driver's primary interaction
 *           surface; satellite imagery with street labels visible
 *           is the right fit for "find a charger near here" use.
 *           Admin uses light-v11 because it's a decorative back-
 *           ground, not an interaction surface, and the cleaner
 *           classic style keeps the chrome readable on top.
 *
 * The Cartographic theme overrides with light-v11 / dark-v11 via
 * its `mapStyle` field — drivers who prefer cartographic roads
 * + labels over satellite imagery pick that theme.
 */
const DEFAULT_STYLE_URLS = {
  dark: 'mapbox://styles/mapbox/standard-satellite',
  light: 'mapbox://styles/mapbox/satellite-streets-v12',
} as const;

export interface PwaMapContainerProps {
  /** Active theme mode. Drives style URL selection. Parent must
   *  also pass `key={mode}` so a mode flip forces a full remount —
   *  mapbox-gl's `style` is constructor-time only, so the map
   *  instance has to be torn down and recreated to apply the new
   *  basemap. Mirrors the admin pattern from 5b082cb. */
  mode: 'light' | 'dark';
  /** Initial camera center [lng, lat]. Read once at mount; subsequent
   *  changes don't reposition (the user's pan/zoom owns the camera
   *  after that). Parent is expected to persist the current camera
   *  via onCameraChange and pass the saved value back on remount. */
  initialCenter: [number, number];
  /** Initial zoom. Read once at mount. */
  initialZoom: number;
  /** Initial pitch (camera tilt, degrees). Read once at mount.
   *  Default 0 (flat-on). Set >0 to open tilted -- e.g. to match the
   *  admin dashboard globe. Pitch gestures stay disabled, so this is a
   *  fixed opening tilt the user pans/zooms within. */
  initialPitch?: number;
  /** Initial bearing (rotation, degrees). Read once at mount.
   *  Default 0 (north-up). */
  initialBearing?: number;
  /** Fires on every `moveend` (after pan / zoom / pitch / rotate
   *  settles) with the current center + zoom. Parent writes this
   *  to a ref so the camera survives `key={mode}` remounts on
   *  theme flips. */
  onCameraChange?: (camera: { center: [number, number]; zoom: number }) => void;
  /** Hands the live mapbox-gl instance up to the parent so sibling
   *  components (marker layer, selection-camera animator, locate-me
   *  FAB, popup renderer) can run imperative side-effects against
   *  the map. Fires with the instance on mount, fires with `null`
   *  on unmount so the parent can gate sibling renders until a fresh
   *  instance lands on the next `key={mode}` remount. */
  onMapReady?: (map: mapboxgl.Map | null) => void;
}

/**
 * Bare mapbox-gl map container for the PWA's /map surface.
 *
 * Phase 1 of the Google Maps -> Mapbox migration. Renders just a
 * styled map at the active mode; no markers, no popups, no search.
 * Phase 2 ports the marker layer (current Google AdvancedMarker +
 * EviMapDot -> mapboxgl.Marker with vanilla DOM); Phase 3 wires
 * search to Mapbox Geocoding; Phase 5 picks up popups + clustering.
 *
 * Construction follows the admin's MapContainer pattern
 * (apps/admin/src/pages/dashboard/widgets/_lib/MapContainer.tsx):
 * raw mapbox-gl (not react-map-gl), idempotent mount guarded by a
 * ref, key-based remount for style swap, cooperativeGestures
 * permanently off so drivers always have free pan/zoom/pinch.
 *
 * Token loaded from VITE_MAPBOX_TOKEN. Missing-token fallback
 * renders a centered instruction message rather than a blank
 * canvas, matching the admin's posture.
 */
export function PwaMapContainer({
  mode,
  initialCenter,
  initialZoom,
  initialPitch = 0,
  initialBearing = 0,
  onCameraChange,
  onMapReady,
}: PwaMapContainerProps) {
  // Theme-aware basemap selection. When the active theme declares
  // a `mapStyle` (e.g., Cartographic theme → light-v11 / dark-v11),
  // use that; otherwise fall back to the satellite defaults that
  // Default + Luce inherit. Read once at mount (the empty-deps
  // effect below applies the URL constructor-time); a theme flip
  // must be paired with `key=` remount upstream — the existing
  // `key={mode}` pattern on MapPage now also keys off theme.id
  // for the same reason.
  const { theme } = useTheme();
  const themeMapStyle = theme.mapStyle;
  const styleUrl = themeMapStyle
    ? themeMapStyle[mode]
    : DEFAULT_STYLE_URLS[mode];
  // Standard-family runtime config (the dusk lightPreset below) is
  // only valid on the standard-satellite style, which exposes the v3
  // `basemap` config schema. Gate on the RESOLVED style URL, not on
  // mode/themeMapStyle: Default dark now declares
  // mapStyle.dark = standard-satellite, and Luce (no mapStyle) falls
  // back to DEFAULT_STYLE_URLS.dark = standard-satellite — both must
  // trip this gate, while any non-standard style (satellite-streets,
  // light-v11, dark-v11, outdoors) must not, or setConfigProperty
  // would throw on a schema it doesn't expose.
  const isStandardSatellite = styleUrl === 'mapbox://styles/mapbox/standard-satellite';

  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  // Latest callbacks in refs so the mount effect reads the current
  // closure without re-binding (and without forcing the effect into
  // a dependency loop that would re-instantiate the map on every
  // parent re-render).
  const onCameraChangeRef = useRef(onCameraChange);
  const onMapReadyRef = useRef(onMapReady);
  useEffect(() => {
    onCameraChangeRef.current = onCameraChange;
  }, [onCameraChange]);
  useEffect(() => {
    onMapReadyRef.current = onMapReady;
  }, [onMapReady]);

  useEffect(() => {
    if (!hostRef.current || !MAPBOX_TOKEN) return;
    // Idempotent — strict-mode double-invoke or HMR remounts during
    // dev must not stack maps in the same host.
    if (mapRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    // The parent checks its camera too; the container never hands mapbox a
    // NaN, swapped or polar centre whatever it is given.
    const startCenter = validCenter(initialCenter, 'initial') ?? NORTH_AMERICA.center;
    const startZoom = Number.isFinite(initialZoom) ? initialZoom : NORTH_AMERICA.zoom;

    const instance = new mapboxgl.Map({
      container: hostRef.current,
      style: styleUrl,
      center: startCenter,
      zoom: startZoom,
      pitch: initialPitch,
      bearing: initialBearing,
      minZoom: 2,
      maxZoom: 18,
      attributionControl: false,
      // Flat charger map: free pan + pinch-zoom, but NO pitch or rotate.
      // touchPitch + dragRotate share the two-finger gesture with zoom, so a
      // pinch with any vertical drift was being read as a pitch instead of a
      // zoom-out. Both are disabled here; twist-rotate is turned off on the
      // touchZoomRotate handler just below so two fingers = zoom only.
      // cooperativeGestures stays off (no "use two fingers" nudge at any
      // viewport; mirrors ecea8b0 on admin).
      dragPan: true,
      touchZoomRotate: true,
      scrollZoom: true,
      touchPitch: false,
      boxZoom: true,
      dragRotate: false,
      pitchWithRotate: false,
      cooperativeGestures: false,
    });

    mapRef.current = instance;

    // Two fingers = zoom only. touchZoomRotate stays enabled for pinch-zoom,
    // but its rotate sub-gesture is disabled so a twisting pinch can't spin
    // the map or be misread as anything but a zoom.
    instance.touchZoomRotate.disableRotation();

    // Size-settle guard (fixes the broken first paint after a theme switch).
    // The map key includes theme.id, so picking a new theme remounts this
    // container and constructs the map WHILE the incoming page is still fading
    // in and the 320ms `evi-theme-transitioning` window is open. mapbox
    // measures the host ONCE at construction; if that reading is off by a
    // frame the globe paints half-initialized (the torn/flattened wedge in the
    // bug report), and a cold (uncached) theme style can leave that first paint
    // stuck until the next full remount — which is exactly why leaving the map
    // and coming back "fixed" it.
    //
    // The host is `position:fixed; inset:0`, so its box size is STABLE: it
    // never emits a later size-CHANGE event. A ResizeObserver + resize-on-load
    // therefore can't recover a wrong-at-construction reading (nothing changes
    // to trigger them), which is why the previous guard still let the broken
    // paint through. So we ALSO force resize() on unconditional deferred beats
    // that fire after both the theme-transition (320ms) and page-fade (~280ms)
    // windows have closed. resize() re-reads the host and forces a full
    // repaint, so it corrects a wrong size AND unsticks a cold first paint.
    // Every beat is idempotent — mapbox no-ops a resize to the same dimensions.
    const resizeToHost = () => {
      if (mapRef.current) mapRef.current.resize();
    };
    instance.on('load', resizeToHost);
    // First fully-rendered frame — the earliest reliable "settled" signal.
    instance.once('idle', resizeToHost);
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(resizeToHost)
      : null;
    if (resizeObserver && hostRef.current) resizeObserver.observe(hostRef.current);

    // Deferred re-fits decoupled from map events: two animation frames (past
    // the first committed paint) and two timers safely past both transition
    // windows. These are what recover the stable-but-wrong-size case the
    // observer can't see.
    const settleTimers: Array<ReturnType<typeof setTimeout>> = [];
    let rafId = requestAnimationFrame(() => {
      resizeToHost();
      rafId = requestAnimationFrame(resizeToHost);
    });
    for (const ms of [250, 550]) settleTimers.push(setTimeout(resizeToHost, ms));

    // Runtime style config — only valid once style.load fires AND
    // only on the standard-satellite style (Default dark + Luce dark
    // fallback resolve to it; see the isStandardSatellite gate above).
    // Classic styles (satellite-streets-v12, light-v11, dark-v11)
    // don't expose the basemap config schema, so calling
    // setConfigProperty on them would throw `Object 'basemap' does not
    // exist in the schema`. Gate the whole block on isStandardSatellite
    // — same four property values + try/catch wrapper as the admin's
    // MapContainer.tsx. Without this block standard-satellite renders
    // with its default daytime lighting preset; 'dusk' is what gives
    // the night-globe look.
    if (isStandardSatellite) {
      instance.on('style.load', () => {
        try {
          instance.setConfigProperty('basemap', 'lightPreset', 'dusk');
          // PWA-specific override of the admin pattern: drivers need
          // navigation context, so road labels + POIs stay visible
          // even on the dark satellite scene. Admin's MapContainer
          // hides all three label types because its map is decorative
          // chrome competing with overlaid widgets; here the map IS
          // the surface, so labels are signal not noise. Transit
          // labels still off — bus / subway / rail icons don't help
          // a driver routing to a charger.
          instance.setConfigProperty('basemap', 'showRoadLabels', true);
          instance.setConfigProperty('basemap', 'showPointOfInterestLabels', true);
          instance.setConfigProperty('basemap', 'showTransitLabels', false);
        } catch {
          // Style doesn't expose the standard basemap schema —
          // safe to ignore; visual identity degrades gracefully.
        }
      });
    }

    // Camera-persist hook — fires after every settled pan/zoom.
    // Parent writes the snapshot to a ref so the next mount (after
    // a `key={mode}` remount on theme flip) can restore the camera
    // via the initialCenter/initialZoom props.
    //
    // Polar guard first. On the globe one hard flick at a low zoom turns the
    // view by tens of degrees, and mapbox lets the camera rest over a pole
    // (how a new driver ended up looking at Antarctica). A camera that
    // settles outside the band for its zoom (clampCameraLat) eases back to
    // the band's edge, keeping longitude and zoom; that ease's own moveend
    // reports the camera.
    const handleMoveEnd = () => {
      const center = instance.getCenter();
      const zoom = instance.getZoom();
      if (!Number.isFinite(center.lat) || !Number.isFinite(center.lng) || !Number.isFinite(zoom)) return;
      const lng = wrapLng(center.lng);
      const lat = clampCameraLat(center.lat, zoom);
      if (lat !== center.lat) {
        instance.easeTo({ center: [lng, lat], duration: 450 });
        return;
      }
      onCameraChangeRef.current?.({
        center: [lng, lat],
        zoom,
      });
    };
    instance.on('moveend', handleMoveEnd);

    // Hand the instance up to the parent so sibling components
    // (marker layer, selection camera, popup renderer, locate FAB)
    // can run imperative side-effects against it.
    onMapReadyRef.current?.(instance);

    return () => {
      instance.off('moveend', handleMoveEnd);
      instance.off('load', resizeToHost);
      instance.off('idle', resizeToHost);
      resizeObserver?.disconnect();
      cancelAnimationFrame(rafId);
      settleTimers.forEach(clearTimeout);
      // Signal teardown so sibling components clear their marker /
      // popup state before the instance is destroyed.
      onMapReadyRef.current?.(null);
      instance.remove();
      mapRef.current = null;
    };
    // Initial camera + style are constructor-time; subsequent changes
    // need a `key={mode}` remount to apply. Empty deps array is
    // intentional — pushing initialCenter / initialZoom / mode into
    // the deps would re-instantiate the map on every parent render.
    // The repo's eslint config doesn't register react-hooks rules,
    // so no disable comment is needed.
  }, []);

  if (!MAPBOX_TOKEN) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--evi-text-mute, #888)',
          padding: 16,
          textAlign: 'center',
          fontFamily: 'var(--font-body)',
          fontSize: 13,
        }}
      >
        Set <code>VITE_MAPBOX_TOKEN</code> in <code>apps/web/.env</code> to display the map.
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      style={{ width: '100%', height: '100%' }}
    />
  );
}
